import * as stylex from '@stylexjs/stylex';

/**
 * 1. MEDIA QUERY CONSTANTS
 */
const MEDIA_1024 = '@media (max-width: 1024px)';
const MEDIA_992 = '@media (max-width: 992px)';
const MEDIA_900 = '@media (max-width: 900px)'; // Added this
const MEDIA_768 = '@media (max-width: 768px)';
const MEDIA_640 = '@media (max-width: 640px)';
const MEDIA_580 = '@media (max-width: 580px)';
const MEDIA_480 = '@media (max-width: 480px)';
const MEDIA_360 = '@media (max-width: 360px)';
const SMALL_MOBILE = MEDIA_480;

/**
 * 2. KEYFRAMES
 */
const rotate = stylex.keyframes({
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
});

// Unused pulse keyframe removed

const float1 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
    '50%': { transform: 'translateY(-20px) rotate(5deg)' },
});

const float2 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(25deg)' },
    '50%': { transform: 'translateY(-15px) rotate(30deg)' },
});

const float3 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(-8deg)' },
    '50%': { transform: 'translateY(-25px) rotate(-3deg)' },
});

const float4 = stylex.keyframes({
    '0%, 100%': { transform: 'translateY(0px) rotate(-12deg)' },
    '50%': { transform: 'translateY(-18px) rotate(-17deg)' },
});

/**
 * 3. STYLES
 */
export const musicStyles = stylex.create({
    modelPage: {
        maxWidth: '1200px',
        marginInline: 'auto',
        padding: {
            default: 'var(--spacing-lg)',
            [MEDIA_1024]: 'var(--spacing-md)',
            [MEDIA_768]: 'var(--spacing-md)',
            [MEDIA_360]: 'var(--spacing-sm)',
        },
        color: 'var(--text-primary)',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: {
            default: 'var(--text-secondary)',
            ':hover': 'var(--primary)',
        },
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transitionProperty: 'color',
        transitionDuration: 'var(--transition-fast)',
    },
    pageTitle: {
        fontSize: '2.5rem',
        fontWeight: 700,
        margin: 0,
        color: 'var(--text-primary)',
    },

    /* Hero Section */
    heroSection: {
        position: 'relative',
        minHeight: {
            default: '600px',
            [MEDIA_1024]: '650px',
            [MEDIA_768]: '650px',
            [MEDIA_640]: '650px',
            [SMALL_MOBILE]: '650px',
            [MEDIA_360]: '400px',
        },
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-3xl)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
        padding: {
            default: 'var(--spacing-2xl)',
            [MEDIA_768]: 'var(--spacing-lg)',
            [MEDIA_640]: 'var(--spacing-lg)',
            [MEDIA_360]: 'var(--spacing-md)',
        },
        maxWidth: {
            default: '600px',
            [MEDIA_768]: '100%',
        },
        color: 'var(--text-primary)',
    },
    heroTitle: {
        fontSize: {
            default: '3rem',
            [MEDIA_900]: '2.5rem',
            [MEDIA_768]: '2.5rem',
            [MEDIA_580]: '2.2rem',
            [MEDIA_480]: '2rem',
            [MEDIA_360]: '1.8rem',
        },
        fontWeight: 800,
        marginBlock: '0 var(--spacing-md)',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    heroDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
    },
    heroStats: {
        display: 'flex',
        gap: 'var(--spacing-lg)',
        flexWrap: 'wrap',
        flexDirection: { [MEDIA_768]: 'column' },
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        fontWeight: 500,
    },
    statSvg: {
        color: 'var(--accent)',
        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
    },
    creditCostBadge: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text-on-accent)',
        paddingBlock: 'var(--spacing-sm)',
        paddingInline: 'var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginTop: 'var(--spacing-md)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        display: 'inline-block',
    },
    heroVoicePlayer: {
        marginTop: { default: 'var(--spacing-md)', [MEDIA_768]: 'var(--spacing-lg)', [MEDIA_480]: 'var(--spacing-md)' },
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
        minHeight: '40px',
        padding: { [MEDIA_768]: 'var(--spacing-md)', [MEDIA_480]: 'var(--spacing-sm)' },
    },
    heroAudioPlayer: {
        width: '100%',
        height: { [MEDIA_768]: '40px', [MEDIA_480]: '36px', [MEDIA_360]: '32px' },
        outline: 'none',
        borderRadius: 'var(--radius-md)',
        '::-webkit-media-controls-panel': {
            borderRadius: 'var(--radius-md)',
            backgroundColor: '#9ca5afff',
            color: 'white',
        },
    },

    musicHeroAnimated: {
        position: 'relative',
        backgroundImage: `linear-gradient(135deg,
            var(--bg-card) 0%,
            rgba(255, 107, 107, 0.02) 25%,
            rgba(255, 193, 7, 0.02) 75%,
            var(--bg-card) 100%)`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        overflow: 'hidden',
        '::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `
        radial-gradient(circle at 20% 50%, rgba(255, 107, 107, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 80% 50%, rgba(255, 193, 7, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 40% 20%, rgba(156, 39, 176, 0.02) 0%, transparent 50%)`,
            opacity: 1,
        },
        '::after': {
            content: '""',
            position: 'absolute',
            inset: '-50%',
            backgroundImage: `
        conic-gradient(from 0deg at 50% 50%,
            transparent 0deg,
            rgba(255, 107, 107, 0.015) 45deg,
            transparent 90deg,
            rgba(255, 193, 7, 0.015) 135deg,
            transparent 180deg,
            rgba(156, 39, 176, 0.015) 225deg,
            transparent 270deg,
            rgba(255, 107, 107, 0.015) 315deg,
            transparent 360deg)`,
            animationName: rotate,
            animationDuration: '120s',
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            opacity: 0.3,
        },
    },

    floatingIconsContainer: {
        position: 'absolute',
        insetBlock: 0,
        right: 0,
        width: '50%',
        pointerEvents: 'none',
        zIndex: 1,
        display: { [MEDIA_992]: 'none' },
    },
    floatingIcon: {
        position: 'absolute',
        color: 'rgba(255, 255, 255, 0.15)',
        opacity: 0.8,
        transitionProperty: 'all',
        transitionDuration: '0.3s',
    },
    floatingIconSvg: {
        filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))',
    },
    floatingIcon1: { top: '15%', right: '20%', animationName: float1, animationDuration: '8s', animationIterationCount: 'infinite', color: 'rgba(255, 107, 107, 0.2)' },
    floatingIcon2: { top: '35%', right: '10%', animationName: float2, animationDuration: '10s', animationIterationCount: 'infinite', color: 'rgba(255, 193, 7, 0.2)' },
    floatingIcon3: { top: '45%', right: '25%', animationName: float3, animationDuration: '12s', animationIterationCount: 'infinite', color: 'rgba(156, 39, 176, 0.2)' },
    floatingIcon4: { top: '60%', right: '15%', animationName: float4, animationDuration: '9s', animationIterationCount: 'infinite', color: 'rgba(76, 175, 80, 0.2)' },

    sectionTitle: {
        fontSize: { default: '2rem', [MEDIA_480]: '1.5rem' },
        fontWeight: 700,
        marginBlock: '0 var(--spacing-xl)',
        color: 'var(--text-primary)',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            width: '60px',
            height: '3px',
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        },
    },
    importantNote: {
        backgroundImage: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(156, 39, 176, 0.1))',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'rgba(255, 107, 107, 0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: { default: 'var(--spacing-xl)', [MEDIA_768]: 'var(--spacing-lg)' },
        marginBottom: 'var(--spacing-xl)',
        display: 'flex',
        gap: 'var(--spacing-lg)',
        alignItems: 'flex-start',
        flexDirection: { [MEDIA_768]: 'column' },
        textAlign: { [MEDIA_768]: 'center' },
    },
    noteIcon: { fontSize: '1.5rem', flexShrink: 0, marginTop: '0.125rem', [MEDIA_768]: { marginTop: 0 } },
    noteHeader: { fontSize: '1.25rem', fontWeight: 600, marginBlock: '0 var(--spacing-sm)', color: 'var(--text-primary)' },
    noteText: { fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 },
    inlineChatButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        backgroundColor: 'var(--bg-secondary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: { default: 'var(--border)', ':hover': 'var(--border-hover)' },
        borderRadius: 'var(--radius-lg)',
        boxShadow: { default: 'var(--shadow-sm)', ':hover': 'var(--shadow-md)' },
        color: { default: 'var(--accent-light)', ':hover': 'var(--aimode)' },
        cursor: 'pointer',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        verticalAlign: 'middle',
        marginInline: '8px',
        flexShrink: 0,
    },

    gallerySection: { marginBottom: 'var(--spacing-3xl)' },
    galleryDescription: {
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textAlign: 'left',
        whiteSpace: 'normal',
    },
    musicGalleryList: {
        display: 'grid',
        gap: 'var(--spacing-md)',
        marginTop: 'var(--spacing-xl)',
        gridTemplateColumns: {
            default: 'none',
            [MEDIA_1024]: 'repeat(auto-fit, minmax(400px, 1fr))',
            [MEDIA_900]: 'repeat(auto-fit, minmax(400px, 1fr))',
            [MEDIA_640]: '1fr', // Force single column on mobile to prevent overflow
            [MEDIA_480]: '1fr',
        },
    },
    musicGalleryItem: {
        backgroundColor: 'var(--bg-card)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-md)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        minHeight: { default: '80px', [MEDIA_900]: '75px', [MEDIA_640]: 'auto', [MEDIA_768]: 'auto' },
        backgroundImage: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(255, 107, 107, 0.02) 100%)',
        flexDirection: { default: 'row', [MEDIA_768]: 'column' }, // Stack vertically on mobile
        gap: { default: 'var(--spacing-sm)', [MEDIA_768]: 'var(--spacing-md)' }, // Consistent gap
        ':hover': {
            transform: 'translateY(-2px)',
            borderColor: 'var(--primary)',
            boxShadow: 'var(--shadow-lg)',
        },
    },
    audioPlayerContainer: {
        flexShrink: 0,
        width: { default: 'max(150px, 45%)', [MEDIA_900]: '140px', [MEDIA_768]: '100%' }, // Full width on mobile
        minWidth: { default: '120px', [MEDIA_768]: 'auto' }, // Remove minWidth constraint on mobile
        order: { [MEDIA_768]: 1 },
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-xs)',
    },
    audioPlayer: {
        width: '100%',
        height: { default: '32px', [MEDIA_640]: '36px', [MEDIA_480]: '32px', [MEDIA_360]: '28px' },
        outline: 'none',
        borderRadius: 'var(--radius-md)',
        '::-webkit-media-controls-panel': {
            borderRadius: 'var(--radius-md)',
            backgroundColor: '#9ca5afff',
            color: 'white',
        },
    },
    musicInfoContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-sm)',
        textAlign: { [MEDIA_768]: 'center', [MEDIA_580]: 'center', [MEDIA_480]: 'center', [MEDIA_640]: 'left' },
        alignItems: { [MEDIA_768]: 'center', [MEDIA_580]: 'center', [MEDIA_640]: 'flex-start' },
    },
    musicTitle: {
        fontSize: 'var(--font-size-md)',
        fontWeight: 600,
        color: 'var(--text-primary)',
        margin: 0,
    },
    musicStyleTags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--spacing-xs)',
    },
    styleTag: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text-on-accent)',
        paddingBlock: 'var(--spacing-xs)',
        paddingInline: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'capitalize',
    },
    modeTag: {
        backgroundColor: 'var(--primary)',
        color: 'var(--text-on-accent)',
        paddingBlock: 'var(--spacing-xs)',
        paddingInline: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'capitalize',
    },
    vocalTag: {
        backgroundColor: 'var(--accent-light)',
        color: 'var(--text-primary)',
        paddingBlock: 'var(--spacing-xs)',
        paddingInline: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'capitalize',
    },
    clickIndicatorMusic: {
        backgroundColor: 'transparent',
        color: 'var(--primary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--primary)',
        paddingBlock: 'var(--spacing-xs)',
        paddingInline: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        fontSize: { default: '0.75rem', [MEDIA_360]: '0.65rem' },
        fontWeight: 500,
        opacity: 0,
        transform: 'scale(0.8)',
        transitionProperty: 'all',
        transitionDuration: 'var(--transition-normal)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        backdropFilter: 'blur(10px)',
        alignSelf: { default: 'flex-start', [MEDIA_768]: 'center', [MEDIA_580]: 'center' },
    },
    indicatorVisible: {
        opacity: 1,
        transform: 'scale(1)',
    }
});